<div class="container">
  <div class="row">
    <div class="col">
      <div
        id="severe-outcomes"
        class="callout__container text-dark"
        style="--callout-accent-color: #06ded8"
      >
        <h2 class="mb-4">Reduce the Risk of Diabetic Ketoacidosis (DKA)</h2>
        <p class="mb-3">
          Type 1 diabetes continues to be commonly misdiagnosed, especially
          among adults, approximately 40% of whom experience an initial
          misdiagnosis.<sup>3,4,5</sup>
        </p>
        <p class="mb-3">
          Both lack of diagnosis and poor disease management from misdiagnosis
          can lead to severe outcomes, including emergency hospitalization as a
          result of DKA. In fact up to 62% of all patients that are diagnosed
          with type 1 diabetes without prior screening are suffering from
          DKA.<sup>1,6</sup>
        </p>
        <p class="mb-3">
          Screening can help identify type 1 diabetes before symptoms appear,
          which may help reduce the risk of DKA at diagnosis.<sup>7,8</sup>
        </p>
        <p class="mb-3">
          Additionally, catching T1D early through screening and management,<sup
            >7</sup
          >
          can help the body "remember" healthier blood sugar patterns, something
          known as a favorable metabolic memory.<sup>8, 9, 10,11</sup> The
          damage caused by high blood sugar levels early on can continue to
          affect the body – even after blood sugar is later brought under better
          control, so early diagnosis and monitoring may reduce the long-term
          health risks associated with poor blood sugar control.<sup>12</sup>
        </p>
        <p class="mb-4">
          Screening early can also help make a difference in patients' lives,
          especially as our understanding of T1D expands and
          <a (click)="navigateTo('https://www.tzield.com/', $event)"
          class="text-decoration-none cursor-pointer"><span class="text-primary">advancements </span></a> are explored.
        </p>
        <div class="callout--mini d-flex flex-column">
          <h3 class="mb-0 text-center text-lg-start text-black">
            An in-office or at-home blood test and monitoring of pre-symptomatic
            T1D patients can
            <span class="text-primary"
              >help reduce DKA rates at Stage 3 T1D onset by as much as
              93.5%.</span
            ><sup>13</sup>
          </h3>
          <p class="mb-0 text-black">
            -- Calculation: 62% DKA in those without screening - 4% DKA in those
            with screening = 58% difference. 58% difference / 62% DKA in those
            with screening = 93.5% reduction from screening. <br />DKA=diabetic
            ketoacidosis; T1D=type 1 diabetes.<br />Sims EK, et al. Diabetes.
            <span class="text-nowrap">2022;71(4):610-623</span>.
          </p>
        </div>
      </div>

      <div
        id="beta-cell-function"
        class="callout__container text-dark"
        style="--callout-accent-color: #9657d6"
      >
        <h2 class="mb-4">
          Beta cell Function and the Role of C-Peptide in T1D
        </h2>
        <p class="mb-3">
          T1D is an autoimmune condition characterized by the progressive and
          irreversible loss of beta cells.<sup>14,15</sup> Although beta cells
          are best known for making insulin,<sup>16</sup> they also have the vital role of
          maintaining glucose homeostasis, impacting a range of the body's
          organs and systems.<sup>17</sup> Exogenous insulin cannot mimic the
          role that beta cells play in maintaining glucose homeostasis.<sup
            >18,19</sup
          >
        </p>
        <p class="mb-3">
          Ongoing, underlying beta-cell destruction by autoreactive T cells is a
          critical marker of progressive autoimmune T1D.<sup>15,20,21</sup>
          Beta-cell destruction may begin as early as 4-6 years before clinical
          symptoms appear.<sup>1</sup> But many patients still have some
          beta-cell function in Stage 3 T1D: beta-cell function decline presents
          differently and happens at different rates.<sup>14, 15</sup>
        </p>
        <p class="mb-3">
          C-peptide is secreted by beta cells at a 1:1 ratio with insulin from
          proinsulin. It mirrors insulin production, and declining levels
          indicate loss of beta-cell function.<sup>22</sup>
        </p>
        <p class="mb-3">
          Beta-cell function, as measured by C-peptide, can indicate patients'
          level of remaining beta-cell activity and help determine the extent of
          autoimmune T1D progression.<sup>15</sup> Even residual beta-cell function can
          reduce risk of T1D complications, including hypoglycemia, retinopathy,
          nephropathy and neuropathy.<sup>9, 19, 23, 24,25 ,26 ,27 ,28 ,29</sup>
        </p>
        <p class="mb-4">
          Read more here about how C-peptide testing can help provide insight
          for patients with T1D. And visit
          <a (click)="navigateTo('https://www.betawayt1d.com/', $event)"
          class="text-decoration-none cursor-pointer"><span class="text-primary">betawayt1d.com</span></a> for more information
          on beta cell function and the role of beta cells in T1D.
        </p>
        <div class="callout--mini d-flex flex-column">
          <h3 class="mb-0 text-center text-lg-start text-black">
            C-Peptide is a
            <span class="text-primary"
              >reliable measure of Beta-cell function</span
            >
            compared to insulin.<sup>22</sup>
          </h3>
        </div>
        <div class="callout__btn mt-3">
          <div
            class="action-plan__btn"
            global-button
            [buttonData]="cardButton"
          ></div>
        </div>
      </div>

      <div
        id="metabolic-memory"
        class="callout__container"
        style="--callout-accent-color: #ff5e2b"
      >
        <h2 class="mb-4 text-dark">
          Metabolic memory and the long-term impact of T1D
        </h2>
        <p class="mb-4 text-dark">
          Metabolic memory describes the lasting influence that early metabolic
          health can have on future outcomes in people with type 1 diabetes.
          Evidence suggests that exposure to hyperglycemia early in the disease
          course may induce persistent cellular and molecular changes that
          continue to influence the risk of long-term complications, even after
          glycemic control improves.<sup>30</sup> Serious, negative, long-term
          impacts associated with DKA include:
        </p>
        <div class="callout--stats">
          <div class="callout--stats__grid">
            <div class="callout--stats__item">
              <img
                src="/assets/img/early-detection/bar chart.svg"
                alt="A decreasing bar graph"
                class="callout--stats__icon"
              />
              <p class="mb-0">
                <b>Brain changes</b> and detrimental neurocognitive outcomes<sup
                  >31</sup
                >
              </p>
            </div>
            <div class="callout--stats__item">
              <img
                src="/assets/img/early-detection/age.svg"
                alt="An hourglass timer"
                class="callout--stats__icon"
              />
              <p class="mb-0">
                <b
                  >A sustained negative effect on glycemic control over time,</b
                >
                independent of other variables<sup>2,32</sup>
              </p>
            </div>
            <div class="callout--stats__item">
              <img
                src="/assets/img/early-detection/exclamation.svg"
                alt="Exclamation points"
                class="callout--stats__icon"
              />
              <p class="mb-0">
                <b>Increased morbidity and mortality</b> that is associated with
                lifelong poor glycemic control<sup>2</sup>
              </p>
            </div>
            <div class="callout--stats__item">
              <img
                src="/assets/img/early-detection/heart.svg"
                alt="A heart"
                class="callout--stats__icon"
              />
              <p class="mb-0">
                <b>Increased risk for cardiomyopathy</b> in young patients with
                DKA<sup>33</sup>
              </p>
            </div>
            <div class="callout--stats__item">
              <img
                src="/assets/img/early-detection/speedometer.svg"
                alt="A scale"
                class="callout--stats__icon"
              />
              <p class="mb-0">
                <b>Psychological distress</b> characterized by ongoing,
                disabling anxiety, intrusive memories of the traumatic DKA
                event, and secondary depression<sup>34</sup>
              </p>
            </div>
          </div>
          <div class="callout--stats__bar d-none d-lg-block"></div>
        </div>

        <div class="callout__metabolic-copy">
          <p class="mb-3 text-dark">
            As our understanding of T1D continues to evolve, increasing
            attention is being placed on the biology of the early disease
            state—when clinicians have the opportunity to characterize disease
            stage and make informed management decisions during a period that
            may have lasting clinical significance.<sup>30</sup>
          </p>
          <p class="mb-0 fw-bold text-dark">
            Spotting diabetes and managing blood sugar levels early on in the
            disease progression may provide long term health benefits for
            patients.
          </p>
        </div>

        <div class="callout__chart-title">
          <h3 class="mb-0 text-center text-lg-start">
            DKA at Diagnosis of T1D Is Associated With Worse Long-Term HbA1C<sup
              >35</sup
            >
          </h3>
        </div>

        <div class="callout--chart mt-3">
          <picture
            [data]="{
              image: 'early-detection/metabolic-memory-graphic',
              webp: true,
              altext: 'A line graph showing DKA severity at diagnosis of children with T1D and long-term glycemic control; HbA1C (%) vs Time since diagnosis (years); The four lines represent % HbA1C increase DKA vs no DKA, Mild/Moderate DKA: 0.9%, Severe DKA: 1.4%, and No DKA; Figure adapted from Duca 2017.',
              format: '',
              width: '946',
              height: '358',
              mobile: false,
              classes: 'img-fluid',
            }"
          ></picture>
        </div>

        <p class="callout__footnote mb-0 mt-3 text-dark">
          *Based on a prospective cohort study of 3364 Colorado residents
          diagnosed with T1D before 18 years of age, in 1998–2012, and monitored
          for up to 15 years. Of those, 1297 (39%) had DKA (defined as blood
          glucose &gt;250 mg/dL, and venous pH &lt;7.3 or bicarbonate &lt;15
          mEq/L) at T1D diagnosis. Severity of DKA available (n=1041) was
          further classified as mild/moderate (pH 7.10–7.29 or bicarbonate 5–14
          mEq/L) (n=693; 67%) or severe (pH &lt;7.10 or bicarbonate &lt;5 mEq/L)
          (n=348; 33%). †P&lt;0.0001 vs no DKA at diagnosis.<sup>24</sup>
          DKA=diabetic ketoacidosis; HbA1C=hemoglobin A1C; T1D=type 1 diabetes.
          Duca LM, et al. Diabetes Care. 2017;40(9):1249-1255.
        </p>
      </div>
    </div>
  </div>
</div>
